import { mailTextLabel } from "@talentkontor/domain";
import { textLabel } from "../../agentur/einstellungen/model";
import type { Pill, Tone } from "../../../model/invoice-view";
import { t } from "../../../lib/sprache";

/**
 * Postausgang der Agentur (Befund 3): jede Mail mit Empfänger, Anlass, Stand, Fehler und Zeit. Reine Anzeige-Logik;
 * die Zeilen liefert public.outbox_list (0041/0056, nur Admins), Aktionen outbox_retry und outbox_cancel.
 *
 * 0056 (Befunde 1, 3, 20): „übersprungen“ trägt einen Grund. Probleme sind nur echte Fehler, Rückläufer und fehlende
 * Adressen; bewusst nicht versendete Mails (Rechnung storniert, Termin abgesagt, Aufgabe erledigt, von Hand verworfen,
 * in der Sammelmail enthalten) stehen unter „Übersprungen“ und lassen sich nicht erneut senden.
 */

export type OutboxStatus = "pending" | "sent" | "failed" | "skipped" | "dry_run" | "bounced" | "held";
export type RecipientKind = "talent" | "staff" | "brand" | "external";
export type SkipReason = "no_address" | "cancelled" | "task_closed" | "manual" | "obsolete" | "digest" | "template_not_approved" | "unverified" | "sample" | "studio_self";

export interface OutboxRow {
  id: string;
  created_at: string;
  sent_at: string | null;
  failed_at: string | null;
  status: OutboxStatus;
  channel: "email" | "whatsapp";
  text_key: string;
  recipient: string;
  recipient_kind: RecipientKind | null;
  language: string;
  attempts: number;
  last_error: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  /** 0348: Belegart (invoice, credit_note, self_billing, self_billing_cancel) */
  invoice_doc?: string | null;
  talent_id: string | null;
  talent_name: string | null;
  notification_type: string | null;
  text_fallback: boolean;
  retryable: boolean;
  retried_at: string | null;
  // 0056
  skip_reason?: SkipReason | null;
  retry_block?: string | null;
  delivery_status?: "delivered" | "delayed" | "bounced" | "complained" | "read" | null;
  delivery_detail?: string | null;
  delivery_at?: string | null;
  detail?: string | null;
  suggested_recipient?: string | null;
  hold_until?: string | null;
  master_kind?: "talent" | "brand" | null;
}

export interface OutboxSummary {
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  dry_run: number;
  bounced?: number;
  no_address?: number;
  held?: number;
  problems?: number;
}

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Anlass einer Mail („1. Mahnung“, „Rechnung an die Marke“); unbekannte eigene Texte der Agentur mit ihrem Schlüssel. */
export function anlass(r: Pick<OutboxRow, "text_key" | "channel">): string {
  if (r.channel === "whatsapp") return `WhatsApp · ${r.text_key.replace(/_/g, " ")}`;
  // Bezeichnung aus den Einstellungen (zweisprachig), für neuere Mailarten aus der Fachlogik
  const own = textLabel(r.text_key);
  return own !== r.text_key ? own : mailTextLabel(r.text_key);
}

/** Anlass mit Einzelheit: bei Aufgaben-Mails die Aufgabe, bei Terminen der Termin (Befund 20). */
export function anlassMitDetail(r: Pick<OutboxRow, "text_key" | "channel" | "detail">): string {
  const a = anlass(r);
  return r.detail ? `${a} · ${r.detail}` : a;
}

const EMPFAENGER: Record<RecipientKind, string> = { talent: "Creator", staff: "Team", get brand() { return t("Marke", "Brand"); }, get external() { return t("Unterzeichner oder Eingeladene", "Signers or invitees"); } };
/** K-450/K-471: Insights-Freigabe geht an die Marke, Belege an die Buchhaltung; beide stehen als externe Empfänger im Postausgang */
export function empfaengerArt(k: RecipientKind | null, textKey?: string | null): string {
  if (textKey === "email.insights_share") return EMPFAENGER.brand;
  if (textKey === "email.accounting_forward" || textKey === "email.accounting_forward_test") return t("Buchhaltung", "Accounting");
  return k ? EMPFAENGER[k] : "Creator";
}

/** Echtes Problem: gescheitert, zurückgekommen oder ohne Adresse. Bewusst übersprungene Mails zählen nicht. */
export function istProblem(r: Pick<OutboxRow, "status" | "skip_reason">): boolean {
  return r.status === "failed" || r.status === "bounced" || (r.status === "skipped" && r.skip_reason === "no_address");
}

const GRUND: Record<SkipReason, string> = {
  get no_address() { return t("Keine Adresse", "No address"); },
  get cancelled() { return t("Storniert oder abgesagt", "Cancelled or called off"); },
  get task_closed() { return t("Aufgabe geschlossen", "Task closed"); },
  get manual() { return t("Verworfen", "Discarded"); },
  get obsolete() { return t("Nicht mehr nötig", "No longer needed"); },
  get digest() { return t("In der Tagesübersicht", "In the daily summary"); },
  // 0180: WhatsApp-Vorlage bei Meta nicht freigegeben (in Prüfung, abgelehnt, pausiert)
  get template_not_approved() { return t("Vorlage nicht freigegeben", "Template not approved"); },
  // 0247/0253 (K-201): Mails nach außen gehen erst mit bestätigter Adresse des Kontos raus
  get unverified() { return t("Zurückgehalten, Adresse unbestätigt", "Held, address not confirmed"); },
  // 0325: Beispieldaten aus dem Schnellstart senden nie etwas
  get sample() { return t("Beispieldaten", "Sample data"); },
  // 0432: Studio, Mail an das eigene Profil mit einer Information, die das Konto schon hat
  get studio_self() { return t("Schon im Konto", "Already in your account"); },
};

export function statusPill(r: Pick<OutboxRow, "status" | "channel"> & Partial<Pick<OutboxRow, "skip_reason" | "delivery_status">>): Pill {
  switch (r.status) {
    case "sent":
      if (r.delivery_status === "complained") return pill(t("Als Spam gemeldet", "Reported as spam"), "error");
      if (r.delivery_status === "read") return pill(t("Gelesen", "Read"), "success");
      if (r.delivery_status === "delivered") return pill(t("Zugestellt", "Delivered"), "success");
      if (r.delivery_status === "delayed") return pill(t("Verzögert", "Delayed"), "warning");
      return pill(t("Versendet", "Sent"), "success");
    case "pending": return pill(t("Wartet auf Versand", "Waiting to be sent"), "info");
    case "held": return pill(t("Wartet auf Tagesübersicht", "Waiting for daily summary"), "info");
    case "failed": return pill(t("Gescheitert", "Failed"), "error");
    case "bounced": return pill(t("Zurückgekommen", "Bounced"), "error");
    case "skipped":
      if (r.skip_reason === "unverified") return pill(t("Zurückgehalten · Adresse unbestätigt", "Held · address not confirmed"), "warning");
      return r.skip_reason === "no_address" ? pill(t("Keine Adresse", "No address"), "error") : pill(t(`Übersprungen${r.skip_reason ? ` · ${GRUND[r.skip_reason]}` : ""}`, `Skipped${r.skip_reason ? ` · ${GRUND[r.skip_reason]}` : ""}`), "neutral");
    default: return pill(r.channel === "whatsapp" ? t("Probelauf, nicht verschickt", "Dry run, not sent") : t("Probelauf", "Dry run"), "neutral");
  }
}

/** Was ist passiert und was kann die Agentur tun (Klartext statt Fehlercode). */
export function erklaerung(r: Pick<OutboxRow, "status" | "last_error" | "retryable" | "text_fallback" | "attempts" | "text_key">
  & Partial<Pick<OutboxRow, "skip_reason" | "retry_block" | "delivery_detail" | "hold_until">>): string | null {
  const e = r.last_error?.trim() || null;
  if (r.status === "failed") {
    if (e && /^Offene Platzhalter/.test(e)) return t(`${e}. Die Mail braucht Werte, die es hier nicht gibt; meist hilft ein Blick in die eigene Fassung unter Einstellungen · Vorlagen, danach erneut senden.`, `${e}. The email needs values that do not exist here; usually a look at your own version under Settings · Templates helps, then send again.`);
    if (!r.retryable) return `${e ?? t("Gescheitert.", "Failed.")} ${r.retry_block ?? t("Der persönliche Link ist nicht mehr gültig: bitte Einladung bzw. Vertrag neu senden.", "The personal link is no longer valid: please resend the invitation or contract.")}`;
    return `${e ?? t("Gescheitert.", "Failed.")}${r.attempts > 1 ? t(` (${r.attempts} Versuche)`, ` (${r.attempts} attempts)`) : ""}`;
  }
  if (r.status === "bounced") {
    return t(`${e ?? t("Die Mail ist zurückgekommen.", "The email bounced.")} Prüfe die Adresse und sende an die richtige Adresse erneut.${!r.retryable && r.retry_block ? ` ${r.retry_block}` : ""}`, `${e ?? t("Die Mail ist zurückgekommen.", "The email bounced.")} Check the address and send again to the correct address.${!r.retryable && r.retry_block ? ` ${r.retry_block}` : ""}`);
  }
  if (r.status === "skipped") {
    if (r.skip_reason === "no_address") return t("Keine E-Mail-Adresse hinterlegt. Trägst du sie im Profil des Creators ein, geht die Mail automatisch raus, solange ihr Anlass noch gilt; sonst hier erneut senden. Das Team hat dazu auch eine Aufgabe.", "No email address on file. Once you enter it in the creator's profile, the email is sent automatically as long as its occasion still applies; otherwise send it again here. The team also has a task for this.");
    if (r.skip_reason === "unverified") return r.retryable
      ? t("Zurückgehalten, solange deine E-Mail-Adresse unbestätigt war. Die Adresse ist jetzt bestätigt: Sende die Mail erneut, wenn ihr Anlass noch gilt.", "Held while your email address was not confirmed. The address is confirmed now: send the email again if its occasion still applies.")
      : t(`Zurückgehalten, bis du deine E-Mail-Adresse bestätigst (Hinweis oben auf der Seite). Danach sendest du sie hier erneut.${r.retry_block && !/bestätigt/.test(r.retry_block) ? ` ${r.retry_block}` : ""}`, `Held until you confirm your email address (notice at the top of the page). Afterwards you can send it again here.${r.retry_block && !/bestätigt/.test(r.retry_block) ? ` ${r.retry_block}` : ""}`);
    if (r.skip_reason === "digest") return t("In der täglichen Übersicht an diese Person enthalten.", "Included in the daily summary to this person.");
    if (r.skip_reason === "template_not_approved") return t(`${e ?? "Die WhatsApp-Vorlage ist bei Meta nicht freigegeben."} Mail und Glocke sind davon unberührt; den Stand der Vorlagen zeigt Einstellungen · WhatsApp.`, `${e ?? "The WhatsApp template is not approved by Meta."} Email and bell are unaffected; Settings · WhatsApp shows the template status.`);
    return t(`${e ?? t("Übersprungen.", "Skipped.")} Bewusst nicht versendet; nichts zu tun.`, `${e ?? t("Übersprungen.", "Skipped.")} Deliberately not sent; nothing to do.`);
  }
  if (r.status === "held") return t("Die Person bekommt Aufgaben und Hinweise einmal am Tag gebündelt; diese Mail kommt in die nächste Übersicht.", "The person receives tasks and notices bundled once a day; this email goes into the next summary.");
  if (r.status === "pending" && e) return t(`Letzter Versuch: ${e}. Der Versand wird wiederholt.`, `Last attempt: ${e}. Sending will be retried.`);
  if (r.status === "sent" && r.delivery_detail) return r.delivery_detail;
  if (r.text_fallback) return t("Standardtext verwendet: die eigene Fassung braucht Angaben, die diese Mail nicht hat (zum Beispiel eine Kooperation).", "Default text used: your own version needs details this email does not have (for example a deal).");
  return null;
}

export type OutboxFilter = "alle" | "probleme" | "wartend" | "versendet" | "uebersprungen";
export const FILTERS: { key: OutboxFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "probleme", get label() { return t("Probleme", "Problems"); } },
  { key: "wartend", get label() { return t("Wartend", "Waiting"); } },
  { key: "versendet", get label() { return t("Versendet", "Sent"); } },
  { key: "uebersprungen", get label() { return t("Bewusst übersprungen", "Deliberately skipped"); } },
];
export function matchesFilter(r: Pick<OutboxRow, "status"> & Partial<Pick<OutboxRow, "skip_reason">>, f: OutboxFilter): boolean {
  if (f === "probleme") return istProblem({ status: r.status, skip_reason: r.skip_reason ?? (r.status === "skipped" ? "no_address" : null) });
  if (f === "wartend") return r.status === "pending" || r.status === "held";
  if (f === "versendet") return r.status === "sent";
  if (f === "uebersprungen") return r.status === "skipped" && r.skip_reason !== "no_address";
  return true;
}
export function searchText(r: OutboxRow): string {
  return [anlassMitDetail(r), r.recipient, r.invoice_number, r.talent_name, r.last_error, r.delivery_detail, empfaengerArt(r.recipient_kind, r.text_key)]
    .filter(Boolean).join(" ").toLowerCase();
}

const MAIL = /^[^@\s"<>,;]+@[^@\s"<>,;]+\.[^@\s"<>,;]+$/;
/** Adresse im Dialog „Erneut senden“ prüfen; leer = die bisherige bzw. hinterlegte Adresse. */
export function pruefeAdresse(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  return MAIL.test(s) ? null : t("Bitte eine gültige E-Mail-Adresse eingeben.", "Please enter a valid email address.");
}

/** 0348: Beleg mit Art und Nummer („Gutschrift GS-2026-003“, „Stornobeleg GS-2026-006“); ohne Angabe wie bisher „Rechnung“ */
export function belegText(r: Pick<OutboxRow, "invoice_number" | "invoice_doc">): string | null {
  if (!r.invoice_number) return null;
  const art = r.invoice_doc === "self_billing" ? t("Gutschrift", "Self-billing invoice")
    : r.invoice_doc === "self_billing_cancel" ? t("Stornobeleg", "Cancellation document")
    : r.invoice_doc === "credit_note" ? t("Stornorechnung", "Cancellation invoice") : t("Rechnung", "Invoice");
  return `${art} ${r.invoice_number}`;
}
